/**
 * V2ResultPill — ResultPillComp and related components
 * Phase 5 (PRI-291): extracted from PrimateVisionV2.tsx
 *
 * Contains: StreamResultCanvas, LocalSlotVideo, ApiResultsTray, CopyPromptButton, ResultPillComp
 */

import { useState, useRef, useEffect, useCallback, memo, type RefObject } from 'react';
import { L, type ResultPill, type PillUsage, fmtTime, stripCompiledQueryEnvelope } from './pv2-shared';
import { INDETERMINATE_COPY, type IndeterminateReason } from '../../lib/result-semantics';
import NarrativeLog from './NarrativeLog';
import type { NarrativeEntry } from '../../lib/narrative';
import { trackResultCopied, trackResultRerun, trackRawApiTrayOpened } from '../../telemetry/events';
import { classifyStreamError } from './V2StreamOverlay';

// PRI-494 P2: track which pills have had the Raw API disclosure opened this session.
// Module-level so the open state persists across re-renders (no lift needed).
const openedRawApiPills = new Set<string>();

// ─────────────────────────────────────────────────────────────────────────────
// BillingMethodLine — PRI-552: terminal settlement summary.
// Renders which metering rule billed this result (per_frame vs per_second)
// plus the processed-frame count. Terminal-only: caller must gate on
// pill.usage != null && !isStreamActive (usage is null until settlement).
// For per_second rows the frame count is shown as an FYI (frame counting
// predates the billing cutover) without implying it was the billing basis.
// ─────────────────────────────────────────────────────────────────────────────
export function BillingMethodLine({ usage }: { usage: PillUsage }) {
  const rule = usage.metering_rule ?? 'per_second';
  const frames = usage.frames;
  const framesLabel = frames != null ? `${frames.toLocaleString('en-US')} frames` : null;
  const text = rule === 'per_frame'
    ? `Billed per frame${framesLabel ? ` · ${framesLabel}` : ''} · ${usage.billed_seconds}s equivalent`
    : `Billed per second · ${usage.billed_seconds}s${framesLabel ? ` (${framesLabel} processed)` : ''}`;
  return (
    <div
      data-testid="billing-method-line"
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontFamily: '"JetBrains Mono", monospace', fontSize: 10,
        color: L.textMuted, letterSpacing: '0.02em',
      }}
    >
      <span aria-hidden="true" style={{ display: 'inline-block', width: 5, height: 5, borderRadius: '50%', background: L.border }} />
      <span>{text}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Stream Result Video - live remoteStream in the result pill
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// StreamResultCanvas — canvas-buffered remote stream display.
//
// Root cause of the flicker: Safari's WebRTC decoder empties its buffer between
// server frames (~2fps inference rate) and the <video> element renders NOTHING —
// the container background (#111) shows through. Chrome holds the last frame;
// Safari doesn't.
//
// Fix: a <canvas> element physically cannot lose its content. It retains the
// last drawn pixels until explicitly cleared. We draw each decoded frame to
// the canvas via requestVideoFrameCallback. Between server frames, the canvas
// holds the last annotated frame — no black flash, ever.
//
// The hidden <video> still decodes the WebRTC stream. The canvas just mirrors
// its content, with the critical difference that it RETAINS frames across gaps.
// ─────────────────────────────────────────────────────────────────────────────

export const StreamResultCanvas = memo(function StreamResultCanvas({ remoteStream, onFirstFrame, shouldMirror = false, isFileStream = false }: { remoteStream: MediaStream; onFirstFrame?: () => void; shouldMirror?: boolean; isFileStream?: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const firedRef = useRef(false);
  const onFirstFrameRef = useRef(onFirstFrame);
   
  onFirstFrameRef.current = onFirstFrame;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Hidden video element — decodes the remote WebRTC stream off-screen.
    const video = document.createElement('video');
    video.srcObject = remoteStream;
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('webkit-playsinline', 'true');
    video.play().catch(() => {});

    const ctx = canvas.getContext('2d')!;
    let raf = 0;
    let lastPresentedFrames = -1;

    // Draw a frame from the hidden video onto the visible canvas.
    // Applies horizontal mirror (webcam convention) and skips
    // black/empty frames so the canvas retains the last valid content.
    function drawValidFrame() {
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      if (vw === 0 || vh === 0) return;

      // Resize canvas to match video dimensions (only when they change)
      if (canvas!.width !== vw || canvas!.height !== vh) {
        // Changing canvas dimensions clears it — re-draw immediately after
        canvas!.width = vw;
        canvas!.height = vh;
      }

      // Quick brightness check: scale video down to 2x2 pixels on a temp
      // region of the canvas, read 4 pixels. This catches the Safari
      // "empty decoder buffer" frames (avg brightness ≈ 0) without the
      // cost of reading full-resolution pixel data.
      ctx.drawImage(video, 0, 0, 2, 2);
      const px = ctx.getImageData(0, 0, 2, 2).data; // 4 pixels × 4 channels = 16 bytes
      let brightness = 0;
      for (let i = 0; i < 16; i += 4) brightness += px[i] + px[i + 1] + px[i + 2];
      brightness /= 4; // average of 4 pixels

      if (brightness < 10) {
        // Black/empty frame — DO NOT draw. Canvas retains last valid frame.
        return;
      }

      // Draw frame — mirror only for webcam selfie view, not file streams
      ctx.save();
      if (shouldMirror) {
        ctx.translate(vw, 0);
        ctx.scale(-1, 1);
      }
      ctx.drawImage(video, 0, 0, vw, vh);
      ctx.restore();

      // Fire onFirstFrame callback once
      if (!firedRef.current && onFirstFrameRef.current) {
        firedRef.current = true;
        onFirstFrameRef.current();
      }
    }

    // Prefer requestVideoFrameCallback — fires only when a new decoded frame
    // is presented (~2fps from server). Much more efficient than rAF polling.
    if ('requestVideoFrameCallback' in video) {
      function onVideoFrame(_now: number, metadata: { presentedFrames: number }) {
        // Skip if same frame was already drawn (shouldn't happen with RVFC, but safety)
        if (metadata.presentedFrames !== lastPresentedFrames) {
          lastPresentedFrames = metadata.presentedFrames;
          drawValidFrame();
        }
        video.requestVideoFrameCallback(onVideoFrame);
      }
      video.requestVideoFrameCallback(onVideoFrame);
    } else {
      // Fallback for browsers without RVFC: poll via requestAnimationFrame.
      // drawValidFrame's brightness check prevents drawing black frames.
      function loop() {
        if (video.readyState >= 2) drawValidFrame();
        raf = requestAnimationFrame(loop);
      }
      raf = requestAnimationFrame(loop);
    }

    return () => {
      cancelAnimationFrame(raf);
      video.srcObject = null;
      video.remove();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteStream]);

  // Reset fired flag when remoteStream changes (new session)
  useEffect(() => { firedRef.current = false; }, [remoteStream]);

  // objectFit does not apply to <canvas> elements (spec only honours it on
  // replaced content: img/video/iframe). For file streams we need natural sizing
  // (max-width/max-height) inside a flex-centred wrapper so the canvas displays
  // at its intrinsic canvas.width×canvas.height ratio, letterboxed within the
  // 16:9 slot. For webcam the canvas fills 100%×100% (cover behaviour).
  if (isFileStream) {
    return (
      <div data-testid="video-slot-remote" style={{
        width: '100%', height: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#000',
      }}>
        <canvas
          ref={canvasRef}
          style={{ maxWidth: '100%', maxHeight: '100%', display: 'block' }}
        />
      </div>
    );
  }
  return (
    <canvas
      ref={canvasRef}
      data-testid="video-slot-remote"
      style={{ width: '100%', height: '100%' }}
    />
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// LocalSlotVideo — local webcam feed in the result pill video slot.
// Memo'd with stable ref to prevent srcObject/play() thrashing on parent re-renders.
// Uses visibility:hidden (not conditional rendering) to stay mounted even when
// the remote stream takes over — prevents black frames from element destroy/create.
// ─────────────────────────────────────────────────────────────────────────────
export const LocalSlotVideo = memo(function LocalSlotVideo({ localStream, hidden = false, isFileStream = false, isFrontCamera = false }: { localStream: MediaStream; hidden?: boolean; isFileStream?: boolean; isFrontCamera?: boolean }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.srcObject = localStream;
    el.muted = true;
    el.play().catch(() => {});
  }, [localStream]);

  // When hidden=true, video plays at opacity:0 (not visibility:hidden — Safari
  // can invalidate the video surface on visibility change). opacity:0 keeps the
  // video composited and painting frames. At landing time, the parent sets
  // opacity:1 via direct DOM manipulation for a seamless crossfade with the clone.
  // Fix #4: Only mirror for webcam (selfie view). File streams are already correct.
  return (
    <video
      ref={videoRef}
      data-testid="video-slot-local"
      data-local-slot-video
      style={{
        position: 'absolute', inset: 0,
        width: '100%', height: '100%',
        objectFit: isFileStream ? 'contain' : 'cover',
        transform: (!isFileStream && isFrontCamera) ? 'scaleX(-1)' : 'none',
        zIndex: 0,
        opacity: hidden ? 0 : 1,
        transition: hidden ? 'none' : 'opacity 0.15s ease',
      }}
      muted autoPlay playsInline
      {...({ 'webkit-playsinline': 'true' } as React.HTMLAttributes<HTMLVideoElement>)}
    />
  );
});


// API Results Tray
// ─────────────────────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function ApiResultsTray({ pill, open, videoRef, isMobile = false, onClose: _onClose }: { pill: ResultPill; open: boolean; videoRef?: RefObject<HTMLVideoElement | null>; isMobile?: boolean; onClose?: () => void }) {
  const isStreamPill = pill.id.startsWith('stream-');
  const jobId = isStreamPill ? pill.id : pill.id.replace('pill-', 'job-');

  // Video playback state
  const [currentTime, setCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  // hasPlayed: true once user presses play; used to switch frame list from 'end' to 'progressive' mode
  const [hasPlayed, setHasPlayed] = useState(false);

  // Use real clips from resultPayload if available, else empty
  // Normalise clips: new API has `confidences` dict, derive scalar `confidence` for display
  // Preserve confidences dict for multi-term display
  const payload = pill.resultPayload ?? {};
  const rawClips = (payload as Record<string, unknown>).clips as Array<{ start_time: number; end_time: number; confidence?: number | null; confidences?: Record<string, number>; [key: string]: unknown }> | null;
  const realClips = Array.isArray(rawClips)
    ? rawClips.map(clip => {
        if (clip.confidences && typeof clip.confidences === 'object') {
          const vals = Object.values(clip.confidences);
          const maxConf = vals.length > 0 ? Math.max(...vals) : 0;
          return { ...clip, confidence: maxConf, confidences: clip.confidences };
        }
        return { ...clip, confidence: clip.confidence ?? 0 };
      })
    : [];
  const streamHistory = pill.streamHistory ?? [];

  // Build stream clips: map frame_num to video timestamps.
  // When recording_elapsed_ms is present (all new sessions), videoDuration is NOT required -
  // each frame carries its own precise recording-relative timestamp.
  // videoDuration is only needed as a fallback for old sessions without recording_elapsed_ms.
  // Include term_confidences for multi-term display.
  const _hasRecordingTs = streamHistory.some(h => h.recording_elapsed_ms != null);
  const streamClips: Array<{ start_time: number; end_time: number; confidence?: number | null; answer?: string; term_confidences?: Record<string, number> }> =
    isStreamPill && pill.videoUrl && streamHistory.length > 0 && (_hasRecordingTs || videoDuration > 0)
      ? (() => {
          const maxFrameNum = streamHistory[streamHistory.length - 1].frame_num;
          const minFrameNum = streamHistory[0].frame_num;
          const frameSpan = maxFrameNum - minFrameNum || 1;
          return streamHistory.map((h, i) => {
            // Prefer recording_elapsed_ms (precise server-synced timing) if available
            const t = h.recording_elapsed_ms != null
              ? h.recording_elapsed_ms / 1000  // ms → seconds
              : ((h.frame_num - minFrameNum) / frameSpan) * videoDuration;  // fallback for old data
            const next = streamHistory[i + 1];
            const end = next
              ? (next.recording_elapsed_ms != null
                  ? next.recording_elapsed_ms / 1000
                  : ((next.frame_num - minFrameNum) / frameSpan) * videoDuration)
              : (videoDuration > 0 ? videoDuration : t + 1);  // last clip: use duration or +1s
            return { start_time: t, end_time: end, confidence: h.confidence, answer: h.answer, term_confidences: h.term_confidences };
          });
        })()
      : [];

  const uploadClips = isStreamPill ? streamClips : realClips;
  const maxConf = uploadClips.length > 0 ? Math.max(...uploadClips.map(c => (c.confidence ?? 0) as number)) : 1;

  // Track video currentTime to highlight the active clip in sync with playback.
  // Effect 1: Attach durationchange/loadedmetadata UNCONDITIONALLY when videoUrl is set.
  // This breaks the deadlock for stream pills: streamClips requires videoDuration > 0 (fallback
  // path), but videoDuration only gets set when this listener fires. By separating duration
  // tracking from the uploadClips guard, we ensure videoDuration is always captured.
  useEffect(() => {
    if (!pill.videoUrl) return;
    let video: HTMLVideoElement | null = null;
    const durationHandler = () => setVideoDuration(video!.duration || 0);

    function attachDuration() {
      const el = videoRef?.current;
      if (!el) return false;
      video = el;
      video.addEventListener('durationchange', durationHandler);
      video.addEventListener('loadedmetadata', durationHandler);
      if (video.duration) setVideoDuration(video.duration);
      return true;
    }

    if (!attachDuration()) {
      const interval = setInterval(() => { if (attachDuration()) clearInterval(interval); }, 200);
      return () => {
        clearInterval(interval);
        if (video) {
          video.removeEventListener('durationchange', durationHandler);
          video.removeEventListener('loadedmetadata', durationHandler);
        }
      };
    }
    return () => {
      if (video) {
        video.removeEventListener('durationchange', durationHandler);
        video.removeEventListener('loadedmetadata', durationHandler);
      }
    };
  }, [videoRef, pill.videoUrl]);  

  // Effect 2: Attach timeupdate + play listeners once clips are available.
  // We can't use videoRef.current as a dependency (refs don't trigger effects),
  // so instead we poll for the video element every 200ms until we attach.
  useEffect(() => {
    if (uploadClips.length === 0) return;
    let video: HTMLVideoElement | null = null;
    const timeHandler = () => setCurrentTime(video!.currentTime);
    const playHandler = () => setHasPlayed(true);

    function attach() {
      const el = videoRef?.current;
      if (!el) return false;
      video = el;
      video.addEventListener('timeupdate', timeHandler);
      video.addEventListener('play', playHandler);
      return true;
    }

    if (!attach()) {
      // Video element not yet mounted (src is null/loading) - poll until available
      const interval = setInterval(() => {
        if (attach()) clearInterval(interval);
      }, 200);
      return () => {
        clearInterval(interval);
        if (video) {
          video.removeEventListener('timeupdate', timeHandler);
          video.removeEventListener('play', playHandler);
        }
      };
    }
    return () => {
      if (video) {
        video.removeEventListener('timeupdate', timeHandler);
        video.removeEventListener('play', playHandler);
      }
    };
  }, [videoRef, isStreamPill, uploadClips.length, pill.videoUrl]);  

  // Active clip index: the clip whose [start_time, end_time) bracket currentTime
  const activeClipIdx = uploadClips.findIndex(
    (c, i) => currentTime >= c.start_time && (currentTime < c.end_time || i === uploadClips.length - 1 && currentTime <= c.end_time)
  );

  const confStr = (pill.confidence / 100).toFixed(2);
  const latency = Math.round(4 + (pill.confidence / 100) * 20);

  // For stream pills: build response from liveData
  const liveConfStr = ((pill.liveData?.confidence ?? 0)).toFixed(4);

  // Build the API response JSON to display.
  // Strip the wire-format envelope from every `query`/`prompt` field here too
  // so the "Show Raw API data" tray never leaks COMPILED_QUERY base64. See
  // pv2-shared.stripCompiledQueryEnvelope for the canonical helper.
  const cleanPillPrompt = stripCompiledQueryEnvelope(pill.prompt);
  const payloadQueryRaw = (payload as Record<string, unknown>).query;
  const cleanPayloadQuery = typeof payloadQueryRaw === 'string'
    ? stripCompiledQueryEnvelope(payloadQueryRaw)
    : payloadQueryRaw;
  const responseJson = isStreamPill
    ? JSON.stringify({
        job_id: jobId,
        status: 'streaming',
        query: cleanPillPrompt,
        answer: pill.liveData?.answer ?? pill.answer,
        confidence: parseFloat(liveConfStr),
        query_type: pill.liveData?.query_type ?? 'object',
        search_term: pill.liveData?.search_term ?? '',
        session_fps: pill.liveData?.session_fps ?? null,
        inference_ms: pill.liveData?.inference_ms ?? null,
        frame_count: streamHistory.length,
        // PRI-552: terminal settlement summary (metering_rule + frames) — only
        // present once GET /v1/streams/{id} usage has landed on the pill.
        ...(pill.usage ? { usage: pill.usage } : {}),
      }, null, 2)
    : JSON.stringify({
        job_id: jobId,
        status: 'complete',
        query: cleanPayloadQuery ?? cleanPillPrompt,
        answer: (payload as Record<string, unknown>).answer ?? pill.answer,
        confidence: parseFloat(confStr),
        query_type: (payload as Record<string, unknown>).query_type ?? pill.query_type ?? 'object',
        ...((payload as Record<string, unknown>).detected_object != null ? { detected_object: (payload as Record<string, unknown>).detected_object } : {}),
        clips: uploadClips,
        // PRI-552: terminal settlement summary (metering_rule + frames) — only
        // present once GET /v1/analyses/{id} usage has landed on the pill.
        ...(pill.usage ? { usage: pill.usage } : {}),
      }, null, 2);

  return (
    <div style={isMobile ? {
      display: open ? 'flex' : 'none',
      flexDirection: 'column',
      width: '100%',
      background: L.bgSurface,
      borderTop: `1px solid ${L.border}`,
      overflowY: 'auto',
    } : {
      display: open ? 'flex' : 'none',
      flexDirection: 'column',
      width: 420,
      flexShrink: 0,
      minWidth: 0,
      background: L.bgSurface,
      borderLeft: `1px solid ${L.border}`,
      overflowY: 'auto',
      alignSelf: 'stretch',
    }}>
                {/* API Request */}
        <div style={{ padding: '12px 14px', borderBottom: `1px solid ${L.border}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: L.textMuted }}>API Request</span>
            <div style={{ flex: 1, height: 1, background: L.border }} />
            <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 8, color: L.signal, background: `${L.signal}14`, padding: '2px 6px', borderRadius: 2 }}>POST /v1/video/analyse</span>
          </div>
          <pre style={{ background: L.bgCode, border: `1px solid ${L.border}`, borderRadius: 4, padding: '10px 12px', fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: L.textPrimary, lineHeight: 1.65, overflowX: 'auto' as const, margin: 0, whiteSpace: 'pre-wrap' as const, wordBreak: 'break-all' as const }}>
{`// Authorization: Bearer pk_••••••••

{
  "video": "${isStreamPill ? 'stream' : 'video.mp4'}",
  "prompt": "${cleanPillPrompt.replace(/"/g, '\\"')}",
  "model": "darwin-preview-1.3B",
  "output_masks": true
}`}
          </pre>
        </div>

        {/* Arrow */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', flexShrink: 0 }}>
          <div style={{ flex: 1, height: 1, background: L.border }} />
          <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: '#16A34A' }}>{isStreamPill ? '↓ streaming...' : `↓ 200 OK · ~${latency}s`}</span>
          <div style={{ flex: 1, height: 1, background: L.border }} />
        </div>

        {/* Response - live updating JSON */}
        <div style={{ padding: '0 14px 12px', borderBottom: `1px solid ${L.border}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: L.textMuted }}>API Response</span>
            <div style={{ flex: 1, height: 1, background: L.border }} />
            {isStreamPill && <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 8, color: '#D97706', background: 'rgba(217,119,6,0.10)', padding: '2px 6px', borderRadius: 2 }}>LIVE</span>}
          </div>
          <pre style={{ background: L.bgCode, border: `1px solid ${L.border}`, borderRadius: 4, padding: '10px 12px', fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: L.textPrimary, lineHeight: 1.65, overflowX: 'auto' as const, margin: 0, whiteSpace: 'pre-wrap' as const, wordBreak: 'break-all' as const }}>
            {responseJson}
          </pre>
        </div>

        {/* Stream history OR clip breakdown */}
        {isStreamPill ? (
          // Stream: frame-by-frame results, synced to video playback when a recording is playing
          <div style={{ padding: '12px 14px', flexShrink: 0 }}>
            <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: L.textMuted, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>{pill.videoUrl ? 'Frame History' : `Frame History (last ${streamHistory.length})`}</span>
              {/* Pulsing dot while video is playing but hasn't reached first frame yet */}
              {pill.videoUrl && streamClips.length > 0 && currentTime > 0 && activeClipIdx === -1 && (
                <span title="Syncing to first API result frame..." style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: L.signal, fontSize: 8 }}>
                  <span style={{ display: 'inline-block', width: 5, height: 5, borderRadius: '50%', background: L.signal, animation: 'pv2-blink 1.2s ease-in-out infinite' }} />
                  <span>syncing...</span>
                </span>
              )}
            </div>
            {streamHistory.length === 0 ? (
              <div style={{ fontSize: 10, color: L.textMuted, fontFamily: '"JetBrains Mono", monospace' }}>Waiting for frames...</div>
             
            // eslint-disable-next-line react-hooks/refs
            ) : (() => {
                // Scrollable container - all frames shown, active frame scrolled into view
                const canSeekFrame = !!pill.videoUrl && !!videoRef?.current && videoDuration > 0;
                const seekToFrame = (frameIdx: number) => {
                  if (!canSeekFrame || !videoRef?.current) return;
                  const t = streamClips[frameIdx]?.start_time ?? 0;
                  videoRef.current.currentTime = t;
                  videoRef.current.pause();
                };
                const activeIdx = activeClipIdx >= 0 ? activeClipIdx : -1;
                // For recorded sessions: show frames chronologically (first at top).
                // For live streams: newest first (reverse).
                const displayFrames = pill.videoUrl
                  ? streamHistory
                  : [...streamHistory].reverse().slice(0, 15);
                return (
                  <div
                    style={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0 }}
                    ref={(el) => {
                      // Auto-scroll to keep active frame visible during playback
                      if (!el || activeIdx < 0 || !hasPlayed) return;
                      const activeEl = el.querySelector(`[data-frame-idx="${activeIdx}"]`) as HTMLElement | null;
                      if (activeEl) activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                    }}
                  >
                    {displayFrames.map((h, i) => {
                      const clipIdx = pill.videoUrl ? i : (streamHistory.length - 1 - i);
                      const isActive = pill.videoUrl && hasPlayed && clipIdx === activeIdx;
                      // Progressive reveal for recorded sessions: only show frames whose
                      // recording timestamp has been reached by the video playhead.
                      // currentTime === 0 means nothing revealed yet (empty list at start).
                      if (pill.videoUrl && hasPlayed) {
                        const frameTime = streamClips[clipIdx]?.start_time ?? 0;
                        if (currentTime < frameTime) return null;
                      } else if (pill.videoUrl && !hasPlayed) {
                        return null;  // hide all frames until play is pressed
                      }
                      const hasMultiTerm = h.term_confidences && typeof h.term_confidences === 'object' && Object.keys(h.term_confidences).length > 1;
                      const termEntries = h.term_confidences ? Object.entries(h.term_confidences).sort((a, b) => b[1] - a[1]) : [];

                      // Multi-term frame display
                      if (hasMultiTerm) {
                        return (
                          <div
                            key={h.frame_num}
                            data-frame-idx={clipIdx}
                            style={{
                              padding: '4px 2px',
                              borderBottom: i < displayFrames.length - 1 ? `1px solid ${L.border}` : 'none',
                              background: isActive ? `${L.signal}14` : 'transparent',
                              borderRadius: 3,
                              flexShrink: 0,
                            }}
                          >
                            {/* Frame header row - clickable to seek */}
                            <div
                              onClick={() => pill.videoUrl ? seekToFrame(clipIdx) : undefined}
                              title={canSeekFrame ? `Seek to frame #${h.frame_num}` : undefined}
                              style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: canSeekFrame ? 'pointer' : 'default', marginBottom: 3 }}
                              onMouseEnter={e => { if (canSeekFrame) (e.currentTarget.parentElement as HTMLElement).style.background = `${L.signal}1a`; }}
                              onMouseLeave={e => { (e.currentTarget.parentElement as HTMLElement).style.background = isActive ? `${L.signal}14` : 'transparent'; }}
                            >
                              <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 9, color: isActive ? L.signal : L.textMuted, width: 48, flexShrink: 0 }}>#{h.frame_num}</span>
                              <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 9, color: L.textPrimary, marginLeft: 'auto' }}>{h.confidence}%</span>
                            </div>
                            {/* Per-term sub-rows */}
                            <div style={{ paddingLeft: 12 }}>
                              {termEntries.map(([term, conf]) => {
                                const termIsYes = conf >= 0.5;
                                const termBarColor = termIsYes ? '#16A34A' : '#DC2626';
                                const pct = Math.round(conf * 100);
                                return (
                                  <div key={term} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '1px 0' }}>
                                    <span style={{
                                      fontFamily: '"JetBrains Mono", monospace', fontSize: 9, color: L.textMuted,
                                      maxWidth: 90, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }} title={term}>{term}</span>
                                    <div style={{ flex: 1, height: 3, background: L.bgCode, borderRadius: 2, overflow: 'hidden' }}>
                                      <div style={{ height: '100%', width: `${pct}%`, background: termBarColor, borderRadius: 2 }} />
                                    </div>
                                    <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 9, color: L.textPrimary, width: 26, textAlign: 'right' as const }}>{pct}%</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      }

                      // Single-term frame display - show term name + confidence bar (no YES/NO)
                      const singleTerm = termEntries.length === 1 ? termEntries[0] : null;
                      const sTermName = singleTerm ? singleTerm[0] : (pill.searchTerms?.[0] ?? '');
                      const sTermConf = singleTerm ? singleTerm[1] : (h.confidence / 100);
                      const sTermPct = singleTerm ? Math.round(sTermConf * 100) : h.confidence;
                      const sTermColor = sTermPct >= 50 ? '#16A34A' : '#DC2626';
                      return (
                        <div
                          key={h.frame_num}
                          data-frame-idx={clipIdx}
                          onClick={() => pill.videoUrl ? seekToFrame(clipIdx) : undefined}
                          title={canSeekFrame ? `Seek to frame #${h.frame_num}` : undefined}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 2px', borderBottom: i < displayFrames.length - 1 ? `1px solid ${L.border}` : 'none', background: isActive ? `${L.signal}14` : 'transparent', borderRadius: 3, cursor: canSeekFrame ? 'pointer' : 'default', flexShrink: 0 }}
                          onMouseEnter={e => { if (canSeekFrame) (e.currentTarget as HTMLElement).style.background = `${L.signal}1a`; }}
                          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = isActive ? `${L.signal}14` : 'transparent'; }}
                        >
                          <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 9, color: isActive ? L.signal : L.textMuted, width: 48, flexShrink: 0 }}>#{h.frame_num}</span>
                          <span style={{
                            fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: L.textMuted,
                            width: 50, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }} title={sTermName}>{sTermName}</span>
                          <div style={{ flex: 1, height: 3, background: L.bgCode, borderRadius: 2, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${sTermPct}%`, background: sTermColor, borderRadius: 2 }} />
                          </div>
                          <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 9, color: L.textPrimary, width: 28, textAlign: 'right' as const }}>{sTermPct}%</span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
          </div>
        ) : (
          // Upload: clip breakdown with multi-term support
          <div style={{ padding: '12px 14px', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: L.textMuted }}>Clip Breakdown</span>
              {/* Show loading indicator while rendered video is being fetched */}
              {pill.videoUrl === null && uploadClips.length > 0 && (
                <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 8, color: L.textMuted, opacity: 0.6, display: 'flex', alignItems: 'center', gap: 3 }}>
                  <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: L.signal, animation: 'pv2-blink 1.2s ease-in-out infinite' }} />
                  loading video...
                </span>
              )}
            </div>
            <div style={{ fontSize: 9, fontFamily: '"JetBrains Mono", monospace', color: L.textMuted, marginBottom: 8 }}>click to seek</div>
            {/* eslint-disable-next-line react-hooks/refs -- videoRef.current accessed in map callback */}
            {uploadClips.map((seg, i) => {
              // Check for multi-term confidences dict
              const clipConfidences = (seg as { confidences?: Record<string, number> }).confidences;
              const hasMultiTerm = clipConfidences && typeof clipConfidences === 'object' && Object.keys(clipConfidences).length > 1;
              const termEntries = clipConfidences ? Object.entries(clipConfidences).sort((a, b) => b[1] - a[1]) : [];

              const norm = (seg.confidence ?? 0) / maxConf;
              const isStrong = norm >= 0.60;
              const isMid = norm >= 0.30;
              const barColor = isStrong ? '#16A34A' : isMid ? '#D97706' : L.border;
              const badge = isStrong
                ? { bg: 'rgba(22,163,74,0.12)', col: '#16A34A', txt: '★' }
                : isMid ? { bg: 'rgba(217,119,6,0.10)', col: '#D97706', txt: '◆' }
                : { bg: L.bgCode, col: L.textMuted, txt: '·' };
              // Clips are only seekable once the rendered video is loaded in the player
              const canSeek = pill.videoUrl !== null && !!videoRef?.current;
              const isActive = i === activeClipIdx;
              // Progressive reveal: only show clips whose start_time has been reached.
              // Empty list at start of playback; clips appear one-by-one as video plays.
              // Before play is pressed (currentTime === 0, hasPlayed === false): show nothing.
              // Once playing: reveal each clip when video reaches its start_time.
              if (!hasPlayed) return null;
              const hasRevealed = currentTime >= seg.start_time;
              if (!hasRevealed) return null;

              // Multi-term display: show clip header + per-term sub-rows
              if (hasMultiTerm) {
                return (
                  <div
                    key={i}
                    style={{
                      minHeight: 48,
                      padding: '5px 4px',
                      borderRadius: 4,
                      borderBottom: i < uploadClips.length - 1 ? `1px solid ${L.border}` : 'none',
                      opacity: pill.videoUrl === null ? 0.55 : 1,
                      transition: 'opacity 300ms, background 150ms',
                      background: isActive ? `${L.signal}14` : 'transparent',
                      outline: isActive ? `1px solid ${L.signal}30` : 'none',
                      animation: 'pv2-fade-up 200ms ease both',
                    }}
                  >
                    {/* Clip header row - clickable to seek */}
                    <div
                      onClick={() => {
                        if (!canSeek) return;
                        const v = videoRef!.current!;
                        v.currentTime = seg.start_time;
                        v.play().catch(() => {});
                      }}
                      title={canSeek ? `Seek to ${fmtTime(seg.start_time)}` : pill.videoUrl === null ? 'Video loading...' : undefined}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4,
                        cursor: canSeek ? 'pointer' : 'default',
                      }}
                    >
                      <span style={{ width: 6, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {isActive && <span style={{ display: 'inline-block', width: 5, height: 5, borderRadius: '50%', background: L.signal }} />}
                      </span>
                      <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 9, color: isActive ? L.textPrimary : L.textMuted, flexShrink: 0 }}>
                        {fmtTime(seg.start_time)}-{fmtTime(seg.end_time)}
                      </span>
                      <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 8, padding: '1px 4px', borderRadius: 2, background: badge.bg, color: badge.col, marginLeft: 'auto' }}>{badge.txt}</span>
                    </div>
                    {/* Per-term sub-rows */}
                    <div style={{ paddingLeft: 12 }}>
                      {termEntries.map(([term, conf]) => {
                        const termIsYes = conf >= 0.5;
                        const termBarColor = termIsYes ? '#16A34A' : '#DC2626';
                        const pct = Math.round(conf * 100);
                        return (
                          <div
                            key={term}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 6,
                              padding: '2px 0',
                            }}
                          >
                            <span style={{
                              fontFamily: '"JetBrains Mono", monospace', fontSize: 11, color: L.textMuted,
                              width: 70, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }} title={term}>{term}</span>
                            <div style={{ flex: 1, height: 4, background: L.bgCode, borderRadius: 2, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${pct}%`, background: termBarColor, borderRadius: 2 }} />
                            </div>
                            <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: L.textPrimary, width: 32, textAlign: 'right' as const }}>{pct}%</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              // Single-term display (backwards compatible - same as before)
              return (
                <div
                  key={i}
                  onClick={() => {
                    if (!canSeek) return;
                    const v = videoRef!.current!;
                    v.currentTime = seg.start_time;
                    v.play().catch(() => {});
                  }}
                  title={canSeek ? `Seek to ${fmtTime(seg.start_time)}` : pill.videoUrl === null ? 'Video loading...' : undefined}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    minHeight: 48,
                    padding: '5px 4px',
                    borderRadius: 4,
                    borderBottom: i < uploadClips.length - 1 ? `1px solid ${L.border}` : 'none',
                    cursor: canSeek ? 'pointer' : 'default',
                    opacity: pill.videoUrl === null ? 0.55 : 1,
                    transition: 'opacity 300ms, background 150ms',
                    background: isActive ? `${L.signal}14` : 'transparent',
                    outline: isActive ? `1px solid ${L.signal}30` : 'none',
                    animation: 'pv2-fade-up 200ms ease both',
                  }}>
                  {/* Playhead indicator */}
                  <span style={{ width: 6, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {isActive && <span style={{ display: 'inline-block', width: 5, height: 5, borderRadius: '50%', background: L.signal }} />}
                  </span>
                  <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 9, color: isActive ? L.textPrimary : L.textMuted, width: 60, flexShrink: 0 }}>{fmtTime(seg.start_time)}-{fmtTime(seg.end_time)}</span>
                  <div style={{ flex: 1, height: 3, background: L.bgCode, borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.round(norm * 100)}%`, background: barColor, borderRadius: 2 }} />
                  </div>
                  <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 9, color: L.textPrimary, width: 26, textAlign: 'right' as const }}>{Math.round(norm * 100)}%</span>
                  <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 8, padding: '1px 4px', borderRadius: 2, background: badge.bg, color: badge.col }}>{badge.txt}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Copy Prompt Button - clipboard icon that fades to a checkmark on success
// ─────────────────────────────────────────────────────────────────────────────

export function CopyPromptButton({ prompt, planTier = 'free' }: { prompt: string; planTier?: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy(e: React.MouseEvent) {
    e.stopPropagation();
    navigator.clipboard.writeText(prompt).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      trackResultCopied({ user_plan: planTier });
    }).catch(() => {/* ignore - clipboard not available */});
  }

  return (
    <button
      onClick={handleCopy}
      aria-label="Copy prompt"
      title={copied ? 'Copied!' : 'Copy prompt'}
      style={{
        flexShrink: 0,
        width: 24, height: 24, borderRadius: 5,
        border: `1px solid ${copied ? 'rgba(22,163,74,0.4)' : L.border}`,
        background: copied ? 'rgba(22,163,74,0.08)' : 'transparent',
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: copied ? '#16A34A' : L.textMuted,
        transition: 'all 150ms',
        padding: 0,
      }}
    >
      {copied ? (
        // Checkmark
        <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
          <path d="M2 5.5l3 3 4-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ) : (
        // Clipboard/copy icon
        <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
          <rect x="3.5" y="3.5" width="6" height="7" rx="1" stroke="currentColor" strokeWidth="1.1"/>
          <path d="M3.5 7.5H2a.5.5 0 01-.5-.5V2A.5.5 0 012 1.5h5a.5.5 0 01.5.5v1.5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
        </svg>
      )}
    </button>
  );
}

// Copy JSON Button — PRI-494 P2: copies result payload JSON inside the Raw API tray
// ─────────────────────────────────────────────────────────────────────────────

export function CopyJsonButton({ json }: { json: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy(e: React.MouseEvent) {
    e.stopPropagation();
    navigator.clipboard.writeText(json).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  }

  return (
    <button
      data-testid="copy-json-btn"
      onClick={handleCopy}
      aria-label={copied ? 'Copied!' : 'Copy JSON'}
      title={copied ? 'Copied!' : 'Copy JSON'}
      style={{
        display: 'flex', alignItems: 'center', gap: 4,
        padding: '4px 8px', borderRadius: 6,
        border: `1px solid ${copied ? 'rgba(22,163,74,0.4)' : L.border}`,
        background: copied ? 'rgba(22,163,74,0.08)' : 'transparent',
        color: copied ? '#16A34A' : L.textMuted,
        cursor: 'pointer', fontSize: 11,
        transition: 'all 150ms',
      }}
    >
      {copied ? (
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path d="M1.5 5l2.5 2.5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ) : (
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <rect x="3" y="3" width="6" height="6.5" rx="1" stroke="currentColor" strokeWidth="1.1"/>
          <path d="M3 6.5H1.5a.5.5 0 01-.5-.5V1.5A.5.5 0 011.5 1H5a.5.5 0 01.5.5V3" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
        </svg>
      )}
      {copied ? 'Copied!' : 'Copy JSON'}
    </button>
  );
}

// Result Pill
// ─────────────────────────────────────────────────────────────────────────────

export function ResultPillComp({ pill, remoteStream, localStream, slotRevealed, noFadeIn, onStop, onPlayVideo, onRerunStream, onDeleteResult, isSaving, isMobile = false, isFrontCamera = false, connectionState, errorMessage, onApiOpenChange, videoSlotRef, isFileStream = false, onDownload, isDownloading = false, canDownload = false, isStreamActive = false, onExpandFullscreen, planTier = 'free', pinnedMode = false, isNewest = true }: {
  pill: ResultPill;
  remoteStream?: MediaStream | null;
  /** Webcam local stream - shown in the slot while remoteStream is unavailable */
  localStream?: MediaStream | null;
  /** When false, the video slot stays transparent and shows no video (fly clone is on top) */
  slotRevealed?: boolean;
  /** Skip the pv2-fade-up entrance animation — pill appears instantly */
  noFadeIn?: boolean;
  onStop?: () => void;
  onPlayVideo?: () => void;
  onRerunStream?: (prompt: string) => void;
  onDeleteResult?: () => void;
  isSaving?: boolean;
  isMobile?: boolean;
  /** True when the active camera is front/selfie — preview is mirrored to match native iOS. */
  isFrontCamera?: boolean;
  connectionState?: string;
  /** When connectionState==='error', show this message in the pill instead of going blank. */
  errorMessage?: string | null;
  onApiOpenChange?: (open: boolean) => void;
  videoSlotRef?: React.Ref<HTMLDivElement | null>;
  /** True when streaming from a file (not webcam). Disables mirror flip. */
  isFileStream?: boolean;
  /** Called when user clicks download. Handler in PrimateVisionV2Inner. */
  onDownload?: () => void;
  /** True while ZIP is being built — shows spinner icon. */
  isDownloading?: boolean;
  /** User's plan tier (e.g. 'free', 'admin'). */
  canDownload?: boolean;
  /** True when a stream or offline job is still in progress for this pill. */
  isStreamActive?: boolean;
  /** Mobile only: open full-screen HUD for the active stream */
  onExpandFullscreen?: () => void;
  /** PRI: analytics tag for copy/rerun events. */
  planTier?: string;
  /** PRI fix/mobile-streaming-3-pinned-zones: when true, pill renders in stripped-down
   * pinned mode — no outer card frame (host provides the container), NarrativeLog fills
   * available height with internal scroll + auto-scroll to latest entry.
   * Desktop is never affected (pinnedMode is only passed from the mobile Zone 2 block). */
  pinnedMode?: boolean;
  /** PRI-494 P2: true for the most-recent non-live pill in the results frame.
   * When false, renders a compact row that expands on tap. */
  isNewest?: boolean;
}) {
  // PRI fix/mobile-streaming-3-pinned-zones: auto-scroll narrative log to
  // latest entry when pinnedMode is active (new entries push old ones upward).
  const pinnedNarrativeScrollRef = useRef<HTMLDivElement | null>(null);
  const narrativeLength = (pill.narrative ?? []).length;
  useEffect(() => {
    const el = pinnedNarrativeScrollRef.current;
    if (!pinnedMode || !el) return;
    // jsdom does not implement Element.scrollTo — guard for test compatibility.
    if (typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    } else {
      el.scrollTop = el.scrollHeight;
    }
  }, [pinnedMode, narrativeLength]);

  // PRI-494 P2: mobile card — per-pill disclosure + compact/expanded state.
  // All hooks must be at top level (before any early returns).
  const [rawApiOpen, setRawApiOpen] = useState(() => openedRawApiPills.has(pill.id));
  // Newest pill starts expanded; older pills start compact (they expand on tap).
  const [mobileExpanded, setMobileExpanded] = useState(() => isNewest !== false);

  const [apiOpen, setApiOpen] = useState(false);
  const setApiOpenWithCallback = (val: boolean | ((prev: boolean) => boolean)) => {
    setApiOpen(prev => {
      const next = typeof val === 'function' ? val(prev) : val;
      onApiOpenChange?.(next);
      return next;
    });
  };

  // Mobile overflow menu (⋯ button) for copy / download / delete on small screens
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [mobileMenuOpen]);

  // Resizable pill height - drag handle on bottom edge
  const MIN_PILL_HEIGHT = 220;
  const [pillHeight, setPillHeight] = useState(MIN_PILL_HEIGHT);
  const contentRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const dragStartYRef = useRef(0);
  const dragStartHeightRef = useRef(MIN_PILL_HEIGHT);

  const handleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    dragStartYRef.current = clientY;
    dragStartHeightRef.current = pillHeight;

    const handleDragMove = (me: MouseEvent | TouchEvent) => {
      if (!isDraggingRef.current) return;
      const y = 'touches' in me ? me.touches[0].clientY : me.clientY;
      const delta = y - dragStartYRef.current;
      const contentEl = contentRef.current;
      // Max = larger of scrollHeight (for offline pills with known content) or 700px
      // For live stream pills, scrollHeight == pillHeight (overflow:hidden clips it),
      // so we use 700 as a reasonable ceiling to allow dragging down.
      const scrollH = contentEl ? contentEl.scrollHeight : 700;
      const maxH = Math.max(scrollH, 700);
      const newH = Math.max(MIN_PILL_HEIGHT, Math.min(dragStartHeightRef.current + delta, maxH));
      setPillHeight(newH);
    };

    const handleDragEnd = () => {
      isDraggingRef.current = false;
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
      document.removeEventListener('touchmove', handleDragMove);
      document.removeEventListener('touchend', handleDragEnd);
    };

    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);
    document.addEventListener('touchmove', handleDragMove, { passive: false });
    document.addEventListener('touchend', handleDragEnd);
  }, [pillHeight]);

  // Double-click handle to reset to default height
  const handleDragDoubleClick = useCallback(() => {
    setPillHeight(MIN_PILL_HEIGHT);
  }, []);
  const isStreamPill = pill.id.startsWith('stream-');
  // Onboarding demo pill (fixture id 'demo-session-001'). The demo's forklift
  // clip must AUTOPLAY (muted + loop, no native controls) so the coaching
  // walkthrough plays the video unattended. With native `controls` + paused
  // the <video> renders Chromium's centered play-button overlay — the lone
  // gray circle Matt flagged 2026-06-04 ("a new circle which was never there
  // before") — and the clip never plays. A muted/looping/controls-less video
  // has no play-button overlay and starts immediately on its own.
  const isDemoPill = pill.id === 'demo-session-001' || pill.id.startsWith('demo-');
  // Show live video when remote inference stream is available.
  // Fall back to local webcam stream while the server connection is establishing —
  // this keeps the same visual feed the user saw in the SourcePreviewCard,
  // exactly matching Matt's "same component video player" requirement.
  const slotIsRevealed   = slotRevealed !== false; // default true for non-pending pills
  const isLiveStreaming  = isStreamPill && isStreamActive;
  // Prefer local video during live streaming for both mobile and desktop.
  // remoteStream is still attached in the background for recording/post-session
  // playback only — it is not shown to the user while the stream is active.
  // PRI-fix/mobile-doubled-camera: on mobile, the fixed top camera zone (MobileLayout.tsx
  // data-testid="mobile-webcam-camera") already renders the localStream feed while the stream
  // is active. Showing LocalSlotVideo in the result pill simultaneously produces a duplicate
  // camera view (Matt screenshot 2026-05-27). Suppress the pill-level local feed only when
  // the top fixed zone is owning that display (isMobile + isLiveStreaming). The result pill
  // can still show StreamResultCanvas for remoteStream (annotated frames) once available,
  // and still MOUNTS LocalSlotVideo (opacity:0) to prevent black-frame re-attach on resume.
  const showLocalFeed    = isStreamPill && localStream != null && slotIsRevealed && !(isMobile && isLiveStreaming);
  const showRemoteStream = isStreamPill && remoteStream != null && slotIsRevealed && !showLocalFeed && !isLiveStreaming;
  // Canvas-buffered approach: LocalSlotVideo is ALWAYS visible (z-index 0).
  // StreamResultCanvas sits on top (z-index 1) and retains the last valid
  // annotated frame between server updates. No remoteFramePainted tracking
  // needed — the canvas handles the transition seamlessly.
  const handleRemoteFirstFrame = useCallback(() => {/* noop — canvas handles visibility */}, []);
  const showLiveVideo    = showLocalFeed || showRemoteStream;
  const videoRef = useRef<HTMLVideoElement>(null);
  // showPlaceholder: true until the video has painted its first frame.
  // We use a separate showPlaceholder flag (not tied to opacity on the video)
  // so the video is always fully visible/interactive from mount - no opacity
  // tricks that interfere with native controls or Safari's play-ready state.
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const prevVideoUrl = useRef<string | null | undefined>(pill.videoUrl);
  // autoPlayOnUrlSet: true when the user clicked the play overlay while videoUrl
  // was null (triggering attachRenderedVideoForced). When videoUrl resolves and the
  // video is ready, we auto-play it - the overlay click IS the play gesture.
  const autoPlayOnUrlSet = useRef(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (prevVideoUrl.current !== pill.videoUrl) {
      prevVideoUrl.current = pill.videoUrl;
       
      setShowPlaceholder(true);
    }
  });

  // Demo pill: keep the forklift clip playing whenever the pill renders.
  // The autoplay loop reveals the result pill by flipping a parent
  // visibility:hidden -> visible during the streaming phase. A muted video
  // that loaded while hidden can stay paused on the first frame in some
  // engines, so we re-arm play() on mount and whenever the demo's narrative
  // pacing advances (visibleNarrativeCount drives pill.narrative.length).
  // play() is a no-op when already playing; muted keeps autoplay allowed.
  const demoNarrativeLen = isDemoPill ? (pill.narrative ?? []).length : 0;
  useEffect(() => {
    if (!isDemoPill || !pill.videoUrl) return;
    const el = videoRef.current;
    if (!el) return;
    el.muted = true;
    el.play().catch(() => {});
  }, [isDemoPill, pill.videoUrl, demoNarrativeLen]);

  // Track video currentTime for NarrativeLog progressive reveal.
  // This mirrors the timeupdate listener in ApiResultsTray but lives here
  // so NarrativeLog always syncs regardless of whether the tray is open.
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  useEffect(() => {
     
    if (!pill.videoUrl) { setVideoCurrentTime(0); return; }
    let vid: HTMLVideoElement | null = null;
    const handler = () => setVideoCurrentTime(vid!.currentTime);
    function attachTime() {
      const el = videoRef.current;
      if (!el) return false;
      vid = el;
      el.addEventListener('timeupdate', handler);
      setVideoCurrentTime(el.currentTime);
      return true;
    }
    if (!attachTime()) {
      const interval = setInterval(() => { if (attachTime()) clearInterval(interval); }, 200);
      return () => {
        clearInterval(interval);
        if (vid) vid.removeEventListener('timeupdate', handler);
      };
    }
    return () => { if (vid) vid.removeEventListener('timeupdate', handler); };
  }, [pill.videoUrl]);  

  // ── Replay prompt tracking ─────────────────────────────────────────────
  // During playback of recorded sessions, scan narrative entries for
  // prompt_change markers and derive the currently-active display prompt.
  // All return paths are funnelled through stripCompiledQueryEnvelope() so a
  // wire-format prompt ("<prompt>|||COMPILED_QUERY:<base64>|||") can never
  // surface in the UI. Sources of leakage: (1) historic DB rows that stored
  // the wire format, (2) replay narrative prompt_change entries captured
  // pre-fix, (3) any future regression in useSubmitHandlers pill creation.
  // The helper is idempotent on already-stripped prompts.
  const isPlayback = !!(pill.videoUrl && !remoteStream);
  const displayPrompt = (() => {
    if (!isPlayback || videoCurrentTime <= 0) return stripCompiledQueryEnvelope(pill.prompt);
    const narrativeEntries = pill.narrative ?? [];
    // Find the last prompt_change entry whose timestamp is <= current playback position
    let lastPromptChange: NarrativeEntry | null = null;
    for (const entry of narrativeEntries) {
      if (entry.type !== 'prompt_change') continue;
      const entryTime = entry.recording_elapsed_ms != null
        ? entry.recording_elapsed_ms / 1000
        : entry.elapsed_s;
      if (entryTime <= videoCurrentTime) {
        // Entries may not be sorted; keep the latest one by timestamp
        if (!lastPromptChange) {
          lastPromptChange = entry;
        } else {
          const prevTime = lastPromptChange.recording_elapsed_ms != null
            ? lastPromptChange.recording_elapsed_ms / 1000
            : lastPromptChange.elapsed_s;
          if (entryTime > prevTime) lastPromptChange = entry;
        }
      }
    }
    if (!lastPromptChange) return stripCompiledQueryEnvelope(pill.prompt);
    // Extract prompt from text like 'Prompt changed to: "new prompt"'
    const match = lastPromptChange.text.match(/Prompt changed to:\s*"(.+)"/);
    return stripCompiledQueryEnvelope(match ? match[1] : pill.prompt);
  })();

  // ── Replay recalculation gap ───────────────────────────────────────────
  // replayRecalculating kept for future use (greying confidence bars during replay recalc gap)

  // PRI fix/mobile-streaming-3-pinned-zones: pinnedMode render
  // Stripped-down render for the fixed Zone 2 active-pill block on mobile.
  // No outer card frame, no video slot, no API tray, no drag handle.
  // Just: prompt header + NarrativeLog (fills height, auto-scrolls) + stop button.
  if (pinnedMode && isMobile) {
    const streamPillId = pill.id;
    const isStreamPillPinned = streamPillId.startsWith('stream-');
    return (
      <div
        data-testid="result-pill-pinned"
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          background: 'transparent',
          overflow: 'hidden',
        }}
      >
        {/* Prompt header row */}
        <div style={{
          padding: '8px 12px',
          borderBottom: `1px solid ${L.border}`,
          fontSize: 13,
          color: L.signal,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          flexShrink: 0,
        }}>
          <span style={{ display: 'block', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <span style={{ opacity: 0.5 }}>&ldquo;</span>
            {stripCompiledQueryEnvelope(pill.prompt)}
            <span style={{ opacity: 0.5 }}>&rdquo;</span>
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            {/* Expand to fullscreen HUD */}
            {onExpandFullscreen && (
              <button
                onClick={onExpandFullscreen}
                aria-label="Full screen"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: L.textMuted, width: 32, height: 32, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', borderRadius: 6,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M1 5V1h4M9 1h4v4M13 9v4H9M5 13H1V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
            {/* Stop button */}
            {isStreamPillPinned && onStop && (
              <button
                onClick={onStop}
                aria-label="Stop stream"
                title="Stop stream"
                style={{
                  background: 'rgba(0,0,0,0.12)', border: 'none', cursor: 'pointer',
                  color: L.textPrimary, width: 32, height: 32, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', borderRadius: 6,
                }}
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <rect x="1.5" y="1.5" width="7" height="7" rx="1" fill="currentColor"/>
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Narrative log — fills remaining height, scrolls internally.
            Old narrative entries scroll off the top as new ones arrive.
            Auto-scroll to bottom handled by pinnedNarrativeScrollRef useEffect. */}
        <div
          ref={pinnedNarrativeScrollRef}
          data-testid="pinned-narrative"
          style={{
            flex: 1,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch' as React.CSSProperties['WebkitOverflowScrolling'],
            padding: '8px 12px',
          }}
        >
          <NarrativeLog
            entries={pill.narrative ?? []}
            isLive={isStreamActive}
            searchTerms={pill.searchTerms}
          />
        </div>
      </div>
    );
  }

  // ── PRI-494 P2: Mobile card variant (isMobile, not pinnedMode) ──────────────
  // Renders the full-card layout for the newest pill, compact row for older pills.
  if (isMobile && !pinnedMode) {
    const isYes = pill.answer === 'YES';
    const isNo = pill.answer === 'NO';
    // pill.confidence is on a 0–100 scale (see confStr above: pill.confidence / 100).
    // Clamp defensively — an unclamped bar width caused a 21,563px horizontal
    // overflow (mobile-pill-horizontal-overflow.spec.ts, PRI-494 P2 regression).
    const confPct = Math.min(100, Math.max(0, Math.round(pill.confidence ?? 0)));
    const chipBg = isYes ? 'rgba(22,163,74,0.12)' : isNo ? 'rgba(220,38,38,0.10)' : L.bgCode;
    const chipCol = isYes ? '#16A34A' : isNo ? '#DC2626' : L.textMuted;

    // Compact row for older (non-newest) pills
    if (!mobileExpanded) {
      return (
        <div
          data-testid="compact-result-row"
          onClick={() => setMobileExpanded(true)}
          role="button"
          tabIndex={0}
          aria-label={`Expand result: ${stripCompiledQueryEnvelope(pill.prompt)}`}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setMobileExpanded(true); }}
          style={{
            width: '100%', maxWidth: 900,
            background: L.bgSurface,
            border: `1px solid ${L.border}`,
            borderRadius: 12,
            display: 'flex', alignItems: 'center',
            gap: 10, padding: '10px 12px',
            cursor: 'pointer',
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
          }}
        >
          {/* Thumbnail */}
          {pill.videoThumb ? (
            <img
              src={pill.videoThumb}
              alt=""
              style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
            />
          ) : (
            <div style={{ width: 44, height: 44, borderRadius: 8, background: L.bgCode, flexShrink: 0 }} />
          )}
          {/* Prompt */}
          <span style={{
            flex: 1, minWidth: 0,
            fontSize: 13, color: L.textPrimary,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {stripCompiledQueryEnvelope(pill.prompt)}
          </span>
          {/* Verdict chip */}
          {pill.answer && (
            <span style={{
              flexShrink: 0, padding: '2px 7px', borderRadius: 6,
              background: chipBg, color: chipCol,
              fontSize: 11, fontWeight: 700,
            }}>{pill.answer}</span>
          )}
          {/* Chevron */}
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" style={{ flexShrink: 0, color: L.textMuted }}>
            <path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      );
    }

    // Full mobile card (expanded — newest or manually expanded)
    const jsonToCopy = JSON.stringify(pill.resultPayload ?? (pill as unknown as Record<string, unknown>).liveData ?? {}, null, 2);

    return (
      <div
        data-testid="result-pill"
        style={{
          width: '100%', maxWidth: 900,
          background: L.bgSurface,
          border: `1px solid ${L.border}`,
          borderRadius: 16,
          boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
          overflow: 'hidden',
          animation: 'pv2-fade-up 250ms ease both',
        }}
      >
      <div
        data-testid="result-pill-body"
        style={{ minWidth: 0, maxWidth: '100%' }}
      >
        {/* Prompt row */}
        <div style={{
          padding: '11px 14px',
          borderBottom: `1px solid ${L.border}`,
          fontSize: 13, color: L.signal,
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', gap: 8,
        }}>
          <span style={{
            flex: 1, minWidth: 0,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            <span style={{ opacity: 0.5 }}>"</span>
            {stripCompiledQueryEnvelope(pill.prompt)}
            <span style={{ opacity: 0.5 }}>"</span>
          </span>
          <div ref={mobileMenuRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
            {/* ⋯ overflow menu for copy/download/delete */}
            <button
              data-testid="pill-overflow-menu-btn"
              aria-label="More options"
              onClick={() => setMobileMenuOpen(o => !o)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: L.textMuted, width: 44, height: 44, minWidth: 44, minHeight: 44,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: 8, padding: 0,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="3" cy="8" r="1.5" fill="currentColor"/>
                <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
                <circle cx="13" cy="8" r="1.5" fill="currentColor"/>
              </svg>
            </button>
            {mobileMenuOpen && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, zIndex: 200,
                background: L.bgSurface, border: `1px solid ${L.border}`,
                borderRadius: 10, padding: '4px 0', minWidth: 160,
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
              }}>
                {/* Copy prompt */}
                <button
                  onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(stripCompiledQueryEnvelope(pill.prompt)).then(() => {}).catch(() => {}); setMobileMenuOpen(false); }}
                  style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '10px 14px', textAlign: 'left', fontSize: 13, color: L.textPrimary, display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  Copy prompt
                </button>
                {/* Download */}
                {onDownload && (
                  <button
                    data-testid="pill-mobile-download-btn"
                    onClick={e => { e.stopPropagation(); if (!isStreamActive && !isDownloading) { onDownload(); } setMobileMenuOpen(false); }}
                    disabled={isStreamActive}
                    style={{ width: '100%', background: 'none', border: 'none', cursor: isStreamActive ? 'default' : 'pointer', padding: '10px 14px', textAlign: 'left', fontSize: 13, color: isStreamActive ? L.textMuted : L.textPrimary, display: 'flex', alignItems: 'center', gap: 8, opacity: isStreamActive ? 0.5 : 1 }}
                  >
                    {isDownloading ? 'Downloading…' : isStreamActive ? 'Available after session' : canDownload ? 'Download' : 'Upgrade to unlock'}
                  </button>
                )}
                {/* Delete */}
                {onDeleteResult && (
                  <button
                    onClick={e => { e.stopPropagation(); if (window.confirm('Delete this result from history?')) onDeleteResult(); setMobileMenuOpen(false); }}
                    style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '10px 14px', textAlign: 'left', fontSize: 13, color: '#e53e3e', display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    Delete result
                  </button>
                )}
              </div>
            )}
            {/* Collapse to compact row if manually expanded (not newest) */}
            {isNewest === false && (
              <button
                aria-label="Collapse result"
                onClick={e => { e.stopPropagation(); setMobileExpanded(false); }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: L.textMuted, width: 32, height: 32, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M3 9l4-4 4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Verdict row */}
        <div
          data-testid="verdict-row"
          aria-live="polite"
          aria-atomic="true"
          style={{
            padding: '10px 14px',
            borderBottom: `1px solid ${L.border}`,
            display: 'flex', alignItems: 'center', gap: 10,
          }}
        >
          {pill.answer ? (
            <>
              <span
                data-testid="verdict-chip"
                style={{
                  padding: '3px 10px', borderRadius: 8,
                  background: chipBg, color: chipCol,
                  fontSize: 12, fontWeight: 700, flexShrink: 0,
                }}
              >{pill.answer}</span>
              {confPct > 0 && (
                <>
                  <span style={{ fontSize: 12, color: L.textMuted, flexShrink: 0 }}>{confPct}%</span>
                  <div style={{ flex: 1, height: 4, background: L.bgCode, borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${confPct}%`, background: chipCol, borderRadius: 2 }} />
                  </div>
                </>
              )}
            </>
          ) : (
            <span style={{ fontSize: 12, color: L.textMuted }}>Analyzing…</span>
          )}
        </div>

        {/* Narrative log — internal scroll, max ~5 entries visible */}
        <div style={{
          maxHeight: 160,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          borderBottom: `1px solid ${L.border}`,
          padding: '8px 14px',
        }}>
          <NarrativeLog
            entries={(pill.narrative ?? []).slice(-20)}
            isLive={isStreamActive}
            searchTerms={pill.searchTerms}
          />
        </div>

        {/* PRI-552: terminal billing summary — shown once usage settles, never mid-session */}
        {pill.usage && !isStreamActive && (
          <div style={{ padding: '8px 14px', borderBottom: `1px solid ${L.border}` }}>
            <BillingMethodLine usage={pill.usage} />
          </div>
        )}

        {/* Raw API disclosure row — closed by default */}
        <div>
          <button
            data-testid="raw-api-disclosure"
            aria-expanded={rawApiOpen}
            onClick={() => {
              const next = !rawApiOpen;
              setRawApiOpen(next);
              if (next) {
                openedRawApiPills.add(pill.id);
                // PRI-552: enrich with the terminal metering_rule (null until usage lands).
                trackRawApiTrayOpened({ pill_id: pill.id, is_stream: pill.id.startsWith('stream-'), metering_rule: pill.usage?.metering_rule ?? null });
              }
            }}
            style={{
              width: '100%', background: 'none', border: 'none',
              cursor: 'pointer', textAlign: 'left',
              padding: '10px 14px',
              display: 'flex', alignItems: 'center', gap: 6,
              color: L.textMuted, fontSize: 12,
              borderTop: rawApiOpen ? `1px solid ${L.border}` : 'none',
            }}
          >
            <span style={{ fontSize: 10 }}>{rawApiOpen ? '▾' : '▸'}</span>
            <span>Raw API data</span>
            <span style={{ opacity: 0.5 }}> — request · response · frames</span>
          </button>

          {rawApiOpen && (
            <div data-testid="api-results-tray" style={{ borderTop: `1px solid ${L.border}`, padding: '0 0 8px' }}>
              {/* Copy JSON button */}
              <div style={{ padding: '8px 14px 4px', display: 'flex', justifyContent: 'flex-end' }}>
                <CopyJsonButton json={jsonToCopy} />
              </div>
              <ApiResultsTray pill={pill} open={rawApiOpen} isMobile />
            </div>
          )}
        </div>
      </div>
      </div>
    );
  }

  return (
    <div data-testid="result-pill" style={{ width: '100%', maxWidth: apiOpen ? '100%' : 900, position: 'relative', animation: noFadeIn ? 'none' : 'pv2-fade-up 250ms ease both', transition: 'max-width 200ms ease' }}>
      <div style={{ background: L.bgSurface, border: `1px solid ${L.border}`, borderRadius: 16, boxShadow: '0 1px 6px rgba(0,0,0,0.06)', position: 'relative', overflow: 'hidden' }}>
        {/* Prompt label - updates during replay when prompt_change entries are crossed */}
        <div style={{ padding: '11px 18px', borderBottom: `1px solid ${L.border}`, fontSize: 13, color: L.signal, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ display: 'block', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', transition: 'opacity 200ms ease' }}>
            <span style={{ opacity: 0.5 }}>"</span>{displayPrompt}<span style={{ opacity: 0.5 }}>"</span>
            {displayPrompt !== pill.prompt && (
              <span style={{ fontSize: 9, marginLeft: 6, color: L.signal, fontFamily: '"JetBrains Mono", monospace', letterSpacing: '0.06em', textTransform: 'uppercase' as const }}>updated</span>
            )}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            {/* Desktop: show all icons inline. Mobile: collapse into ⋯ overflow menu. */}
            {!isMobile && (
              <>
                <CopyPromptButton prompt={displayPrompt} planTier={planTier} />
                {/* Download button */}
                {onDownload && (
                  <button
                    data-testid="pill-download-btn"
                    aria-label="Download results"
                    title={isStreamActive ? 'Available after session ends' : canDownload ? 'Download results' : 'Upgrade to download'}
                    onClick={() => { if (!isStreamActive && !isDownloading) onDownload(); }}
                    style={{ background: 'none', border: 'none', padding: '3px 5px', display: 'flex', alignItems: 'center', borderRadius: 4, lineHeight: 1, cursor: (isStreamActive || isDownloading) ? 'default' : 'pointer', color: L.textMuted, opacity: (isStreamActive) ? 0.35 : 1 }}
                    onMouseEnter={e => { if (!isStreamActive && !isDownloading) (e.currentTarget as HTMLElement).style.color = L.textPrimary; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = L.textMuted; }}
                  >
                    {isDownloading ? (
                      // Spinner
                      <svg width="11" height="11" viewBox="0 0 12 12" fill="none" style={{ animation: 'pv2-spin 0.8s linear infinite' }}>
                        <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.5" strokeDasharray="14 6" strokeLinecap="round"/>
                      </svg>
                    ) : canDownload ? (
                      // Download arrow icon
                      <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                        <path d="M6 1v7M3 5.5l3 3 3-3M2 10h8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      // Lock icon (free users)
                      <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                        <rect x="2" y="5.5" width="8" height="5.5" rx="1" stroke="currentColor" strokeWidth="1.2"/>
                        <path d="M4 5.5V4a2 2 0 014 0v1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                      </svg>
                    )}
                  </button>
                )}
                {onDeleteResult && (
                  <button
                    title="Delete this result"
                    aria-label="Delete result"
                    onClick={() => { if (window.confirm('Delete this result from history?')) onDeleteResult(); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: L.textMuted, padding: '3px 5px', display: 'flex', alignItems: 'center', borderRadius: 4, lineHeight: 1 }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#e53e3e'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = L.textMuted; }}
                  >
                    <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                      <path d="M1 3h10M4 3V2h4v1M2 3l.8 7.2A1 1 0 003.8 11h4.4a1 1 0 001-.8L10 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                )}
              </>
            )}
            {/* Mobile: ⋯ overflow menu */}
            {isMobile && (
              <div ref={mobileMenuRef} style={{ position: 'relative' }}>
                <button
                  data-testid="pill-overflow-menu-btn"
                  aria-label="More options"
                  onClick={() => setMobileMenuOpen(o => !o)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: L.textMuted,
                    width: 44,
                    height: 44,
                    minWidth: 44,
                    minHeight: 44,
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 20,
                    lineHeight: 1,
                    borderRadius: 8,
                  }}
                >⋯</button>
                {mobileMenuOpen && (
                  <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, background: L.bgSurface, border: `1px solid ${L.border}`, borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 100, minWidth: 180, overflow: 'hidden' }}>
                    {/* Copy */}
                    <button
                      onClick={() => { navigator.clipboard.writeText(displayPrompt).catch(() => {}); setMobileMenuOpen(false); }}
                      style={{ width: '100%', background: 'none', border: 'none', padding: '10px 14px', textAlign: 'left', fontSize: 13, color: L.textPrimary, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><rect x="1" y="3" width="7" height="8" rx="1" stroke="currentColor" strokeWidth="1.2"/><path d="M4 1h6a1 1 0 011 1v8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
                      Copy prompt
                    </button>
                    {/* Download */}
                    {onDownload && (
                      <button
                        data-testid="pill-mobile-download-btn"
                        onClick={() => { if (!isStreamActive && !isDownloading) { onDownload(); setMobileMenuOpen(false); } }}
                        style={{ width: '100%', background: 'none', border: 'none', padding: '10px 14px', textAlign: 'left', fontSize: 13, color: (isStreamActive || isDownloading) ? L.textMuted : L.textPrimary, cursor: (isStreamActive || isDownloading) ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                      >
                        {isDownloading ? (
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ animation: 'pv2-spin 0.8s linear infinite' }}><circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.5" strokeDasharray="14 6" strokeLinecap="round"/></svg>
                        ) : canDownload ? (
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 1v7M3 5.5l3 3 3-3M2 10h8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        ) : (
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><rect x="2" y="5.5" width="8" height="5.5" rx="1" stroke="currentColor" strokeWidth="1.2"/><path d="M4 5.5V4a2 2 0 014 0v1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
                        )}
                        {isDownloading ? 'Downloading…' : isStreamActive ? 'Download (available after session)' : canDownload ? 'Download results' : 'Download (upgrade to unlock)'}
                      </button>
                    )}
                    {/* Delete */}
                    {onDeleteResult && (
                      <button
                        onClick={() => { setMobileMenuOpen(false); if (window.confirm('Delete this result from history?')) onDeleteResult(); }}
                        style={{ width: '100%', background: 'none', border: 'none', padding: '10px 14px', textAlign: 'left', fontSize: 13, color: '#e53e3e', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, borderTop: `1px solid ${L.border}` }}
                      >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 3h10M4 3V2h4v1M2 3l.8 7.2A1 1 0 003.8 11h4.4a1 1 0 001-.8L10 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        Delete result
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div ref={contentRef} style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'stretch', position: 'relative', overflow: 'hidden', height: isMobile ? undefined : pillHeight }}>
          {/* Video side
              Sizing strategy: padding-top 56.25% (= 9/16) inside a position:relative
              wrapper gives a true cross-browser 16:9 box that works in Safari.
              aspectRatio + minHeight on a flex item breaks in WebKit when the
              minHeight wins and the aspect ratio is ignored. The padding trick
              is universally supported.

              Play strategy: the video is always visible (no opacity:0 phase).
              A dark placeholder div sits behind it (zIndex 0). The placeholder
              is hidden once the first frame paints (onLoadedData), which does
              NOT seek and does NOT interfere with the browser's play-ready state.
              This ensures a single click on the native controls plays the video. */}
          {/* On mobile during live streaming, the fixed camera zone (MobileLayout) already
              owns the local feed display. Rendering this in-pill video slot would produce
              a duplicate black 16:9 box labelled "Live stream · no recording". Hide the
              slot with display:none (not unmount) so:
                - No black box / wasted space — narrative + prompt slide up naturally
                - LocalSlotVideo stays mounted (preserves black-frame-safe video element)
                - Fullscreen button stays in DOM (required by overlay tests)
              Desktop and replay (non-live) cases are unaffected. */}
          <div data-testid="video-slot-container" ref={videoSlotRef} style={{
            width: isMobile ? '100%' : 312, flexShrink: 0,
            background: slotIsRevealed ? '#111' : 'transparent',
            position: 'relative',
            // Hide border + content until slot is revealed (fly clone covers this area until then)
            borderRight: slotIsRevealed ? (isMobile ? 'none' : `1px solid ${L.border}`) : 'none',
            borderBottom: slotIsRevealed ? (isMobile ? `1px solid ${L.border}` : 'none') : 'none',
            borderRadius: isMobile ? '0' : '0 0 0 16px',
            overflow: 'hidden',
            // Collapse slot entirely on mobile during live streaming — takes no space,
            // but DOM subtree (incl. LocalSlotVideo) stays mounted to avoid black frames.
            display: (isMobile && isLiveStreaming) ? 'none' : undefined,
          }}>
            {/* 16:9 aspect-ratio sizer - padding-top trick works in all browsers */}
            <div style={{ paddingTop: '56.25%', position: 'relative', minHeight: isMobile ? 0 : 220 }}>
            {/* Local feed — ALWAYS mounted when localStream exists (no conditional
                 unmount). Uses visibility:hidden instead of conditional rendering to
                 avoid destroying/recreating the video element, which causes black frames.
                 Positioned behind the remote stream (z-index 0 vs 1). */}
            {isStreamPill && localStream && (
              <LocalSlotVideo localStream={localStream} hidden={!slotIsRevealed || (isMobile && isLiveStreaming)} isFileStream={isFileStream} isFrontCamera={isFrontCamera} />
            )}
            {/* File stream thumbnail placeholder — shown in the slot while the file
                 captureStream is being set up. Once localStream arrives, LocalSlotVideo
                 layers on top (both at z-index 0, but video covers the image). */}
            {isStreamPill && !localStream && pill.videoThumb && (
              <img src={pill.videoThumb} alt="" style={{
                position: 'absolute', inset: 0, width: '100%', height: '100%',
                objectFit: 'contain', zIndex: 0,
                opacity: slotIsRevealed ? 1 : 0,
              }} />
            )}
            {showRemoteStream ? (
              // Remote inference stream — layered above local stream (z-index 1).
              <div style={{ position: 'absolute', inset: 0, zIndex: 1 }}>
                <StreamResultCanvas remoteStream={remoteStream!} onFirstFrame={handleRemoteFirstFrame} shouldMirror={!isFileStream && isFrontCamera} isFileStream={isFileStream} />
              </div>
            ) : !showLocalFeed && pill.videoUrl ? (
              <>
                {/* Dark placeholder behind the video - always mounted, hidden once
                    first frame paints. zIndex 0 so it sits under the video. */}
                {showPlaceholder && (
                  pill.videoThumb
                    ? <img src={pill.videoThumb} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', zIndex: 0, background: '#000' }} />
                    : <div style={{ position: 'absolute', inset: 0, background: '#1a1a1a', zIndex: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: L.signal, animation: 'pv2-blink 1.2s ease-in-out infinite' }} />
                      </div>
                )}
<video
                  ref={el => {
                    // Assign to the forwarded ref (used by ApiResultsTray for clip seeking)
                    (videoRef as React.MutableRefObject<HTMLVideoElement | null>).current = el;
                    if (el) {
                      // Safari requires muted set imperatively via DOM property.
                      // React's boolean `muted` prop is not reliably reflected as
                      // the HTML muted attribute in WebKit - setting it here in the
                      // ref callback ensures it is applied synchronously on mount,
                      // before any user interaction. Without this, Safari sometimes
                      // treats the first tap as activating audio (un-muting) instead
                      // of starting playback - causing the two-click-to-play issue.
                      el.muted = true;
                    }
                  }}
                  src={pill.videoUrl}
                  // Demo pill: autoplay muted + loop with NO native controls so
                  // the clip plays unattended in the onboarding walkthrough and
                  // never shows Chromium's centered play-button overlay (the lone
                  // gray circle). Real result pills keep native controls + manual
                  // play (controls let the user scrub/seek the analysed clip).
                  controls={!isDemoPill}
                  autoPlay={isDemoPill}
                  loop={isDemoPill}
                  muted={isDemoPill}
                  playsInline
                  preload="auto"
                  poster={pill.videoThumb || undefined}
                  onLoadedData={() => {
                    // onLoadedData fires when the first frame is available to render.
                    // Unlike onLoadedMetadata (no frame) or onCanPlay (may seek),
                    // this is the earliest safe point to hide the placeholder.
                    // We do NOT seek here - no seek = no interference with
                    // the browser's play-ready state = single click to play.
                    setShowPlaceholder(false);
                    // Demo pill: kick off autoplay defensively. The `autoPlay`
                    // attribute alone can be dropped by the browser when the
                    // element mounts inside a visibility:hidden container (the
                    // onboarding reserve slot) and isn't always re-armed when it
                    // becomes visible. An explicit muted play() on first frame is
                    // reliable and is a no-op if already playing.
                    if (isDemoPill && videoRef.current) {
                      videoRef.current.muted = true;
                      videoRef.current.play().catch(() => {});
                    }
                    // If the user clicked the play overlay while videoUrl was null
                    // (e.g. loading from side nav before URL resolved), auto-play
                    // now so the overlay click counts as the play gesture.
                    if (autoPlayOnUrlSet.current && videoRef.current) {
                      autoPlayOnUrlSet.current = false;
                      videoRef.current.muted = true;
                      videoRef.current.play().catch(() => {});
                    }
                  }}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    zIndex: 1,
                    background: 'transparent',
                    // Stream recordings are saved already mirrored (webcam-style) via the
                    // canvas-mirror pipeline in useStreamSession. No CSS flip needed here -
                    // applying scaleX(-1) to a <video> with native controls flips the
                    // play triangle and scrubber too, which looks broken.
                    // File uploads and offline-processed videos are never flipped.
                    // Video is always fully visible - no opacity trick.
                    // Placeholder sits behind (zIndex 0) and is removed on first frame.
                  }}
                  {...({ 'webkit-playsinline': 'true' } as React.HTMLAttributes<HTMLVideoElement>)}
                />
              </>
            ) : pill.videoThumb && !showLocalFeed ? (
              // Fall-through thumbnail — only when there's no live local feed.
              // Without the `!showLocalFeed` guard, this img painted over
              // LocalSlotVideo on file-source pills with samEnabled=false,
              // because the LocalSlotVideo sits at zIndex:0 and this <img>
              // had no zIndex and rendered later in DOM order. Result: user
              // only saw the static thumbnail and never the playing file
              // video. Regression covered by file-pill-playback specs.
              <img src={pill.videoThumb} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', background: '#000' }} />
            ) : isStreamPill && !showLiveVideo && slotIsRevealed && !onRerunStream ? (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#1a1a1a' }}>
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <rect x="2" y="7" width="18" height="14" rx="2" stroke="#555" strokeWidth="1.5"/>
                  <path d="M20 11l6-3v12l-6-3V11z" stroke="#555" strokeWidth="1.5" strokeLinejoin="round"/>
                </svg>
                <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: '#555', letterSpacing: '0.06em' }}>Live stream · no recording</span>
              </div>
            ) : null}
            </div>
            {/* Play button overlay - non-stream pills without video OR historical stream pills (re-run) */}
            {isStreamPill && !showLiveVideo && slotIsRevealed && onRerunStream && (
              <div
                onClick={() => {
                  trackResultRerun({ user_plan: planTier });
                  onRerunStream(pill.prompt);
                }}
                title="No recording saved - click to re-run this prompt as a new live stream"
                style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'rgba(0,0,0,0.35)', cursor: 'pointer' }}
              >
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 2l10 5-10 5V2z" fill="white"/></svg>
                </div>
                <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 9, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Re-run stream</span>
              </div>
            )}
            {/* Fullscreen expander — mobile live stream only */}
            {isMobile && isStreamActive && onExpandFullscreen && (
              <button
                onClick={onExpandFullscreen}
                aria-label="Full screen"
                style={{
                  position: 'absolute', top: 8, right: 8, zIndex: 10,
                  width: 32, height: 32, borderRadius: 8,
                  background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M1 5V1h4M9 1h4v4M13 9v4H9M5 13H1V9" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
            {!isStreamPill && !pill.videoUrl && (
              <div
                data-testid="video-slot-placeholder"
                onClick={() => {
                  if (onPlayVideo) {
                    // Arm auto-play: when the videoUrl resolves and the video
                    // element mounts, play it immediately (single-click UX).
                    autoPlayOnUrlSet.current = true;
                    onPlayVideo();
                  }
                }}
                style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, background: 'rgba(0,0,0,0.15)', cursor: onPlayVideo ? 'pointer' : 'default' }}
              >
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 2l10 5-10 5V2z" fill="white"/></svg>
                </div>
                {/* Onboarding demo placeholder: when there is no real video to play
                    (no onPlayVideo handler from the host), label the slot so it's
                    obvious the player is intentionally empty. Matt 2026-05-26:
                    "on the video player (currently a black square) please put
                    some placeholder text over it that says 'video coming soon'". */}
                {!onPlayVideo && (
                  <span
                    data-testid="video-slot-coming-soon"
                    style={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: 10,
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      color: 'rgba(255,255,255,0.78)',
                      textShadow: '0 1px 2px rgba(0,0,0,0.45)',
                      pointerEvents: 'none',
                    }}
                  >
                    Video coming soon
                  </span>
                )}
              </div>
            )}
            {/* Saving overlay - shown while recording is uploading */}
            {isSaving && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'rgba(0,0,0,0.55)', pointerEvents: 'none' }}>
                <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#fff', animation: 'pv2-blink 1.2s ease-in-out infinite' }} />
                <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, color: '#fff', letterSpacing: '0.08em' }}>Saving recording...</span>
              </div>
            )}
            {/* ■ stop button - visible once video is showing */}
            {isStreamPill && onStop && (
              <button
                onClick={onStop}
                aria-label="Stop stream"
                title="Stop stream"
                style={{
                  position: 'absolute', top: 8, right: 8, zIndex: 10,
                  width: 24, height: 24, borderRadius: 6,
                  background: 'rgba(0,0,0,0.45)', border: 'none',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', flexShrink: 0,
                }}
              >
                {/* Solid square = universal "stop" symbol */}
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <rect x="1.5" y="1.5" width="7" height="7" rx="1" fill="currentColor"/>
                </svg>
              </button>
            )}
          </div>

          {/* Result side — always visible. Shows 'Starting inference...' immediately
               on submit so the user has feedback while the fly animation plays. */}
          {/* minWidth fix (Matt 2026-06-02, mobile pill wider than viewport):
               On desktop the body row is flexDirection:'row' and minWidth:280 keeps
               the result column readable next to the 312px video slot. On MOBILE the
               row is flexDirection:'column', where minWidth:280 instead pins the child's
               HORIZONTAL min-size to 280px — on a ~320–375px viewport (minus card border
               + the parent's own padding) the child can't shrink, forcing horizontal
               overflow so the whole pill renders wider than the screen. Use minWidth:0
               on mobile so the column child shrinks to the available width. */}
          <div data-testid="result-pill-body" style={{
            flex: 1, minWidth: isMobile ? 0 : 280, maxWidth: '100%', padding: '20px 22px',
            display: 'flex', flexDirection: 'column', gap: 10,
            position: 'relative', borderRadius: '0 0 16px 0', overflow: 'hidden',
          }}>


            {/* Pre-narrative confidence indicator - shows live per-term confidence
               bars while waiting for the first narrative entry to arrive */}
            {(() => {
              const isStreamPillInner = pill.id.startsWith('stream-');
              const hasNarrative = (pill.narrative ?? []).length > 0;
              // Show pre-narrative status for ALL active stream states (including connecting/idle
              // for pending pills that were created before the stream actually connects).
              // Error state: show error message in pill instead of going blank.
              // PRI-414: streamPillIdRef is nulled on error, so the live
              // errorMessage prop is gone by the time the errored pill
              // re-renders — fall back to the error stamped onto the pill
              // itself (useStreamLifecycle) so the narrative survives.
              const stampedError = pill.streamErrorMessage ?? null;
              if (isStreamPillInner && !hasNarrative && (connectionState === 'error' || stampedError)) {
                const rawError = errorMessage ?? stampedError;
                const msg = rawError
                  ? classifyStreamError(rawError).displayMsg
                  : 'Inference stopped unexpectedly. Please retry.';
                return (
                  <div style={{ padding: '4px 0' }}>
                    <span data-testid="stream-pill-error" style={{ fontSize: 13, color: '#EF4444', fontFamily: 'Inter, system-ui, sans-serif', lineHeight: 1.5 }}>
                      {msg}
                    </span>
                  </div>
                );
              }

              const activeStreamStates = ['idle', 'connecting', 'queued', 'buffering', 'live', 'recalculating'];
              const showPreNarrative = isStreamPillInner && !hasNarrative &&
                activeStreamStates.includes(connectionState ?? '');
              const isRecalculating = connectionState === 'recalculating';
              const isPreConnect = connectionState === 'idle' || connectionState === 'connecting' || connectionState === 'queued' || connectionState === 'buffering';
              if (!showPreNarrative) return null;

              const termConf = (pill.liveData as { term_confidences?: Record<string, number> } | undefined)?.term_confidences;
              const termEntries = termConf ? Object.entries(termConf).sort((a, b) => b[1] - a[1]) : [];

              // PRI-374: deterministic hooks for the synthetic demo-readiness monitor.
              //  - data-inference-live="true" once we've left the pre-connect/
              //    "Starting inference..." state (i.e. real inference is flowing).
              //  - data-testid="live-term-confidences" wraps the per-term bars,
              //    which only render when real inference metadata (term_confidences)
              //    has arrived from the backend. A dead backend never renders these,
              //    so the synthetic monitor can hard-assert a TRUE first frame
              //    instead of accepting a merely-"streamy" UI state.
              const inferenceLive = !isPreConnect && !isRecalculating;
              return (
                <div
                  data-testid="live-inference-status"
                  data-inference-live={inferenceLive ? 'true' : 'false'}
                  style={{ display: 'flex', flexDirection: 'column', gap: 6, opacity: isRecalculating ? 0.4 : 1, transition: 'opacity 300ms ease' }}
                >
                  <span style={{ fontSize: 11, color: L.textMuted, fontFamily: 'Inter, system-ui, sans-serif', letterSpacing: '0.05em' }}>
                    {isRecalculating ? 'Recalculating...' : isPreConnect ? 'Starting inference...' : 'Analyzing...'}
                  </span>
                  {termEntries.length > 0 && (
                    <span data-testid="live-term-confidences" style={{ display: 'none' }} aria-hidden="true" />
                  )}
                  {termEntries.map(([term, conf]) => {
                    const pct = Math.round(conf * 100);
                    const barColor = pct >= 50 ? '#16A34A' : pct >= 30 ? '#D97706' : '#DC2626';
                    return (
                      <div key={term} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 12, color: L.textPrimary, fontFamily: 'Inter, system-ui, sans-serif', width: 80, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {term}
                        </span>
                        <div style={{ flex: 1, height: 4, background: L.bgCode, borderRadius: 2, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: 2, transition: 'width 300ms ease, background 300ms ease' }} />
                        </div>
                        <span style={{ fontSize: 11, color: L.textMuted, fontFamily: '"JetBrains Mono", monospace', width: 32, textAlign: 'right' as const }}>
                          {pct}%
                        </span>
                      </div>
                    );
                  })}
                  {termEntries.length === 0 && !isPreConnect && (
                    <span style={{ fontSize: 12, color: L.textMuted, fontFamily: 'Inter, system-ui, sans-serif' }}>
                      Starting analysis...
                    </span>
                  )}
                </div>
              );
            })()}

            {/* Answer line (PRI-488 F2): detected_count for count queries +
                actionable indeterminate_reason copy — mirrors the public API's
                result semantics. Offline analyses only (stream pills carry the
                live narrative instead). */}
            {(() => {
              if (pill.id.startsWith('stream-')) return null;
              const rp = (pill.resultPayload ?? {}) as Record<string, unknown>;
              const reason = rp.indeterminate_reason as IndeterminateReason | null | undefined;
              const count = rp.detected_count as number | null | undefined;
              const rawAnswer = typeof rp.answer === 'string' ? rp.answer.toLowerCase().trim() : null;
              if (reason) {
                return (
                  <div data-testid="result-indeterminate-reason" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#D97706', fontFamily: 'Inter, system-ui, sans-serif' }}>
                      Indeterminate
                    </span>
                    <span style={{ fontSize: 12, color: L.textMuted, fontFamily: 'Inter, system-ui, sans-serif', lineHeight: 1.5 }}>
                      {INDETERMINATE_COPY[reason] ?? 'The model could not commit to an answer.'}
                      {/* PRI-516: unsupported/low-confidence paths link to the honest capability list. */}
                      {(reason === 'unsupported_query_form' || reason === 'low_confidence') && (
                        <>
                          {' '}
                          <a
                            href="/docs/supported-actions"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="supported-actions-link"
                            style={{ color: L.signal, textDecoration: 'underline' }}
                          >
                            See what Darwin can detect
                          </a>
                        </>
                      )}
                    </span>
                  </div>
                );
              }
              if (count != null && (rawAnswer === 'yes' || rawAnswer === 'no')) {
                return (
                  <div data-testid="result-detected-count" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: rawAnswer === 'yes' ? '#16A34A' : L.textPrimary, fontFamily: 'Inter, system-ui, sans-serif' }}>
                      {rawAnswer} — {count} detected
                    </span>
                    <span style={{ fontSize: 11, color: L.textMuted, fontFamily: 'Inter, system-ui, sans-serif' }}>
                      Confidence applies to the count itself.
                    </span>
                  </div>
                );
              }
              return null;
            })()}

            {/* Narrative log - replaces YES/NO badge */}
            <NarrativeLog
              entries={pill.narrative ?? []}
              isLive={pill.id.startsWith('stream-') && remoteStream != null}
              isPlayback={!!(pill.videoUrl && !remoteStream)}
              currentVideoTime={pill.videoUrl && !remoteStream ? videoCurrentTime : undefined}
              searchTerms={pill.searchTerms}
              videoRef={videoRef as React.RefObject<HTMLVideoElement | null>}
            />

            {/* Result text (offline analyses) — REMOVED 2026-05-26 (jeff):
                The <p>{pill.resultText}</p> render that lived here duplicated the
                action-chip text (e.g. "Forklift moving") as a second line below the
                NarrativeLog. The action chip at the top of the input bar is the
                canonical render; the narrative entries carry the per-frame narrative
                text. The duplicate line was visual noise on every result card.
                Regression covered by V2ResultPill.test.tsx ("does NOT render
                pill.resultText") and onboarding-no-duplicate-resulttext.spec.ts. */}

            {/* Footer: gear (quality badge removed from results card chrome — PRI-437 P8: quality tiers no longer exist)
                PRI-552: BillingMethodLine on the left — terminal-only (usage null mid-session). */}
            <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              {pill.usage && !isStreamActive ? <BillingMethodLine usage={pill.usage} /> : <span />}
              <button
                onClick={() => setApiOpenWithCallback(o => !o)}
                title={apiOpen ? 'Hide API results' : 'Show API results'}
                style={{
                  height: 24, padding: '0 8px', borderRadius: 6,
                  border: `1px solid ${apiOpen ? L.signal : L.border}`,
                  background: apiOpen ? `${L.signal}12` : 'transparent',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 3,
                  color: apiOpen ? L.signal : L.textMuted,
                  fontSize: 11, fontWeight: 500,
                  transition: 'all 150ms',
                  whiteSpace: 'nowrap',
                }}
              >
                Show Raw API data
              </button>
            </div>
          </div>

          {/* API results tray - slides from right edge */}
          <ApiResultsTray pill={pill} open={apiOpen} videoRef={videoRef} isMobile={isMobile} onClose={() => setApiOpenWithCallback(false)} />
        </div>

        {/* Drag handle on bottom edge - desktop only */}
        {!isMobile && (
          <div
            data-testid="pill-drag-handle"
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
            onDoubleClick={handleDragDoubleClick}
            title="Drag to resize · double-click to reset"
            style={{
              height: 12,
              cursor: 'ns-resize',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderTop: `1px solid ${L.border}`,
              background: L.bgSurface,
              borderRadius: '0 0 16px 16px',
              flexShrink: 0,
              userSelect: 'none',
            }}
          >
            {/* Grip dots */}
            <div style={{
              width: 32, height: 3, borderRadius: 2,
              background: L.textMuted, opacity: 0.25,
            }} />
          </div>
        )}
      </div>
    </div>
  );
}
